import React, { PureComponent, Fragment } from 'react';
import { withRouter, Redirect } from 'react-router';

import classNames from 'classnames';
import {
  Button,
  Empty,
  ErrorBoundary,
  Fetcher,
  Field,
  Form,
  Icon,
  Image,
  Input,
  Radio,
  Search,
  Spinner
} from 'components/ClubComponents';
import { Grid } from 'components/grid';
import { PreviewPageThumbnail } from 'components/svg/images';
import { I18n, translate } from 'components/translate';
import { ROUTES } from 'configs/routes/constants';
import ClubDate from 'helpers/ClubDate';
import { MembershipConfigWatcher, CONDITION } from 'hoc/withMembershipConfig';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import ContentPreventLeave from 'modules/content/components/ContentPreventLeave';
import BackgroundUpload from 'modules/content/components/media/BackgroundUpload';
import ContentPageService from 'modules/content/ContentPageService';
import ContentService from 'modules/content/ContentService';
import { FileService } from 'modules/file';
import { ENTITY_TYPE, MAX_PAGE_BACKGROUND_SIZE } from 'modules/file/constants';
import { WEBINAR_LIVE } from 'modules/membership/constants';
import MembershipService from 'modules/membership/MembershipService';
import { UiService } from 'modules/ui';
import QueryString from 'query-string';

import { AddWebinarPropTypes } from './prop-types';
import WebinarSide from './webinar-side.svg';
import WebinarActions from './WebinarActions';

import ContentBasePage from '../../ContentBasePage';
import { getHotmartWebinarUrl } from '../helpers';
import WebinarService, { GET_WEBINARS_URL } from '../WebinarService';

import './AddWebinar.scss';

const PLUS_ICON = <Icon name="plus" />;

const FORM_SCHEMA = {
  name: 'required',
  webinar: 'required',
  thumbnail: 'required'
};

const ACTION_TYPES = {
  CREATE: 'create',
  UPDATE: 'update'
};

const FETCHER_CONFIG = {
  url: GET_WEBINARS_URL
};

const CONDITIONS = [{ configs: [WEBINAR_LIVE], condition: CONDITION.NONE }];
const INTENTION = {
  BACK: 'back',
  NEW: 'new'
};

@translate()
@withRouter
class AddWebinar extends PureComponent {
  // #region setup
  constructor(props) {
    super(props);

    this.initialState = {
      errors: {},
      fields: {
        name: '',
        webinar: '',
        thumbnail: {}
      },
      loaded: true,
      newWebinarPreventLeave: false,
      status: null,
      webinar: {},
      webinars: null,
      intention: ''
    };

    this.state = { ...this.initialState };

    this.moduleId = null;

    this.form = React.createRef();
    this.fetcher = React.createRef();
    this.fetchWebinars = this.fetchWebinars.bind(this);

    this.key = 1;
  }

  componentDidMount() {
    const { moduleId } = this.props.match.params;

    this.moduleId = moduleId;
    ContentService.lastEditedModule = moduleId;

    this.setInitialStateAsync();
  }

  componentDidUpdate(prevProps) {
    const { id } = this.props.match.params || {};
    const { id: prevId } = prevProps.match.params || {};

    if (id && id !== prevId) {
      this.setInitialStateAsync();
    } else if (!id && id !== prevId) {
      this.clear();
    }
  }

  async setInitialStateAsync() {
    const { id } = this.props.match.params || {};

    if (id) {
      const webinar = await WebinarService.getById(id);

      this.key += 1;

      this.setState({
        webinar,
        fields: {
          name: webinar.name,
          webinar: webinar.webinarKey,
          thumbnail: webinar.thumbnail
        }
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    UiService.smoothScroll('body', 500);
  }
  // #endregion

  // #region page actions
  get actions() {
    const actions = {};
    const { id } = this.props.match.params || {};

    if (id) {
      actions.onPublish = this.handlePublish.bind(this);
      actions.onVisualize = this.handleVisualize.bind(this);
      actions.onNew = this.beforeHandleNew.bind(this);
    }

    return actions;
  }

  beforeHandleNew = () => {
    if (!this.isDirty) {
      this.handleAddNew();
    } else {
      this.setState({ intention: INTENTION.NEW, newWebinarPreventLeave: true });
    }
  };

  handleAddNew = () => {
    const { history } = this.props;

    this.setState({ newWebinarPreventLeave: false });
    this.clear();

    setTimeout(() => {
      const newWebinar = ROUTES.CONTENT.NEW_WEBINAR.replace(
        ':moduleId',
        this.moduleId
      );

      history.push(`${newWebinar}${window.location.search}`);
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    }, 100);
  };

  navigateToNew = () => {
    const { history } = this.props;

    this.setState({ newWebinarPreventLeave: false });
    this.clear();

    setTimeout(() => {
      const newWebinar = ROUTES.CONTENT.NEW_WEBINAR.replace(
        ':moduleId',
        this.moduleId
      );

      history.push(`${newWebinar}${window.location.search}`);
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    }, 100);
  };

  navigateToModulesList = () => {
    const { history } = this.props;

    history.push(`${ROUTES.CONTENT.LIST}${history.location.search}`);
  };

  handleVisualize() {
    const { id } = this.state.webinar || {};

    ContentPageService.contentPreview(id);
  }

  async handlePublish() {
    this.toggleLoader();

    const { webinar = {} } = this.state;
    const { id, published } = webinar;
    const modules = null;
    const showAlert = false;
    const publishedNewState = !published;

    const response = await ContentPageService.publish(
      [id],
      publishedNewState,
      modules,
      showAlert
    );

    await ContentService.fetchModulesList(this.seasonId);

    if (response) {
      this.setState({ webinar: { ...webinar, published: publishedNewState } });
    }

    await this.form.current.submit();

    this.toggleLoader();
  }

  discardThenNavigate = async () => {
    const { intention } = this.state;

    this.setState(
      {
        fields: { ...this.snapshot }
      },
      () => {
        if (intention === INTENTION.BACK) {
          this.navigateToModulesList();
        } else {
          this.navigateToNew();
        }
      }
    );
  };

  saveThenNavigate = async () => {
    const { intention } = this.state;

    try {
      await this.form.current.submit();

      if (intention === INTENTION.BACK) {
        this.navigateToModulesList();
      } else {
        this.navigateToNew();
      }
    } catch {
      this.setState({ intention: '', newWebinarPreventLeave: false });
    }
  };

  back = () => {
    if (!this.isDirty) {
      this.navigateToModulesList();
    } else {
      this.setState({
        intention: INTENTION.BACK,
        newWebinarPreventLeave: true
      });
    }
  };

  handleSave = async ({ fields }) => {
    this.toggleLoader();

    const actionType = this.actionType;
    const { id } = this.state.webinar;

    const response = await WebinarService[actionType]({
      ...fields,
      ...{ id },
      moduleId: this.moduleId
    });

    if (!response) {
      this.toggleLoader();

      throw Error(`Failed to ${actionType} webinar`);
    }

    this.setState(({ webinar }) => ({
      webinar: {
        ...webinar,
        name: fields.name,
        webinarKey: fields.webinar,
        thumbnail: fields.thumbnail
      }
    }));

    await ContentService.fetchModulesList(this.seasonId, this.extra);

    const { history } = this.props;
    const editModule = ROUTES.CONTENT.EDIT_WEBINAR.replace(
      ':moduleId',
      this.moduleId
    ).replace(':id', response.id);

    if (actionType === ACTION_TYPES.CREATE) {
      history.replace(`${editModule}${window.location.search}`);
    }

    this.toggleLoader();
  };

  handleSaveThenReturn = () => {
    this.setState(
      {
        intention: INTENTION.BACK
      },
      this.saveThenNavigate
    );
  };

  // #endregion

  // #region handlers
  get actionType() {
    const { id } = this.state.webinar || {};

    return id ? ACTION_TYPES.UPDATE : ACTION_TYPES.CREATE;
  }

  get seasonId() {
    const qs = QueryString.parse(location.search);

    return qs.season ? qs.season : null;
  }

  get extra() {
    const qs = QueryString.parse(location.search);

    return qs.extra ? qs.extra : false;
  }

  get isDirty() {
    const { fields = {} } = this.state;

    return ContentPreventLeave.isDirty(this.snapshot, fields);
  }

  get snapshot() {
    const { webinar = {} } = this.state;

    return {
      name: webinar.name || '',
      webinar: webinar.webinarKey || '',
      thumbnail: webinar.thumbnail || {}
    };
  }

  clear() {
    this.key += 1;

    this.setState({ ...this.initialState });
  }

  fetchWebinars = () => {
    return WebinarService.getWebinars({ completed: false });
  };

  refreshWebinars = () => {
    this.fetcher.current.retry({ config: {} });
  };

  parseFetchWebinarsResponse = (response) => {
    const { data } = response || {};
    const { data: webinars = [] } = data || {};

    return {
      webinars
    };
  };

  handleFetchWebinarsSuccess = ({ response }) => {
    const { webinars } = response || {};

    this.setState({
      webinars,
      currentWebinars: webinars
    });
  };

  handleFetchWebinarsError = ({ retry }) => {
    return (
      <div className="cc-fetcher__error">
        <p>
          <I18n label="general.retry" />
        </p>
        <Button.Primary onClick={retry}>
          <Icon name="redo" />
        </Button.Primary>
      </div>
    );
  };

  handleCancel = () => {
    this.setState({ newWebinarPreventLeave: false });
  };

  handleChange = ({ fields }) => {
    this.setState({ fields: { ...fields } });
  };

  onSearchWebinar = ({ value: query }) => {
    const { webinars } = this.state;

    if (!query) {
      this.setState({ currentWebinars: webinars });
    }

    const currentWebinars = webinars.filter(({ name }) => {
      const regex = new RegExp(query, 'gi');

      return name && name.match(regex);
    });

    this.setState({ currentWebinars });
  };

  toggleLoader = () => {
    this.setState((prevState) => ({ loaded: !prevState.loaded }));
  };
  // #endregion

  // #region render
  renderHeader() {
    const { webinar = {} } = this.state;

    return (
      <ContentBasePage.Header title="webinar_editor.title">
        <ContentBasePage.Header.Actions>
          <ContentBasePage.Header.Actions.Default
            isPublished={webinar.published}
            {...this.actions}
          />
        </ContentBasePage.Header.Actions>
      </ContentBasePage.Header>
    );
  }

  renderWebinarsFieldLabel = () => (
    <Fragment>
      <I18n label="webinar_editor.webinars" />
      <Button.Secondary
        className="add-webinar__refresh-list"
        outline
        paddingless
        icon="redo"
        onClick={this.refreshWebinars}
      >
        <I18n label="webinar_editor.refresh_list" />
      </Button.Secondary>
    </Fragment>
  );

  renderFetcherChildren =
    (onChange) =>
    ({ loading }) => {
      const { webinars } = this.state;
      const shouldRegisterWebinar = isEmpty(webinars);
      const isLoading = loading && isNil(webinars);

      if (isLoading) {
        return <Spinner solid mask />;
      }

      return (
        <Fragment>
          {!shouldRegisterWebinar && this.renderWebinarList(onChange)}
          {shouldRegisterWebinar && this.renderRegisterWebinar()}
        </Fragment>
      );
    };

  renderRegisterWebinar = () => {
    return (
      <Empty className="add-webinar__list-empty">
        <div className="add-webinar__list-empty__description">
          <h3>
            <I18n label="webinar_editor.webinars_list.create_your_webinar" />
          </h3>
          <p>
            <I18n label="webinar_editor.webinars_list.instructions" />
          </p>
          <a
            className="add-webinar__link-hotmart-webinar cc-button is-primary is-large"
            target="_blank"
            rel="noopener noreferrer"
            href={getHotmartWebinarUrl()}
            alt="Link to Hotmar webinar"
          >
            <I18n label="webinar_editor.webinars_list.access_hotmart_webinar" />
          </a>
        </div>
        <Image src={WebinarSide} alt="Webinar artwork" />
      </Empty>
    );
  };

  renderWebinarListTools = () => {
    const { t } = this.props;

    return (
      <div className="add-webinar__list__tools">
        <Search
          autoComplete="off"
          className="add-webinar__list__tools__search"
          icon="search"
          name="name"
          onSearch={this.onSearchWebinar}
          placeholder={t('webinar_editor.webinars_list.search_placeholder')}
          wait={300}
        />
        <a
          className="add-webinar__list__tools__add-new-webinar cc-button is-primary is-large"
          target="_blank"
          rel="noopener noreferrer"
          href={getHotmartWebinarUrl()}
          alt="Link to Hotmar webinar"
        >
          {PLUS_ICON}
          <I18n label="webinar_editor.webinars_list.access_hotmart_webinar" />
        </a>
      </div>
    );
  };

  renderThumbnailImage = (webinar) => {
    const { largeThumbnailLink, name } = webinar || {};

    if (largeThumbnailLink) {
      return (
        <Image
          className="webinar__details__thumbnail"
          fit={Image.FIT.COVER}
          src={largeThumbnailLink}
          alt={name}
        />
      );
    }

    return (
      <div className="placeholder">
        <Icon name="video2" size={Icon.SIZE.MEDIUM} />
      </div>
    );
  };

  renderWebinar = (webinar) => {
    const { t } = this.props;
    const { fields } = this.state;
    const date = new ClubDate(new Date(webinar.liveStartAt));
    const checked = webinar.code === fields.webinar;

    return (
      <Radio
        key={webinar.id}
        className={classNames('o-list-item', 'webinar', {
          'webinar--selected': checked
        })}
        name="webinar"
        value={webinar.code}
        checked={checked}
      >
        <div className="webinar__details">
          {this.renderThumbnailImage(webinar)}
          <div className="description">
            <b>{webinar.name}</b>
            {webinar.liveStartAt && (
              <p className="date u-is-marginless">{`${date.format('dddd', {
                locale: ClubDate.locale
              })}, ${date.format()} ${t('general.at')} ${date.format(
                'HH:mm'
              )}`}</p>
            )}
          </div>
        </div>
      </Radio>
    );
  };

  renderWebinarList = (onChange) => {
    const { currentWebinars } = this.state;
    const hasWebinars = currentWebinars && currentWebinars.length > 0;

    return (
      <div className="add-webinar__list">
        {this.renderWebinarListTools()}
        <fieldset
          className="o-list -hoverable add-webinar__list__webinars"
          onChange={onChange}
        >
          {hasWebinars
            ? currentWebinars.map(this.renderWebinar)
            : this.renderWebinarListEmpty()}
        </fieldset>
      </div>
    );
  };

  renderWebinarListEmpty = () => {
    const { t } = this.props;

    return (
      <Empty>
        <Icon name="empty" size={Icon.SIZE.LARGE} />
        <p>{t('general.no_result_found')}</p>
      </Empty>
    );
  };

  renderPreventLeave() {
    const { fields = {}, newWebinarPreventLeave } = this.state;

    return (
      <Fragment>
        <ContentPreventLeave
          current={fields}
          onConfirm={this.handleSaveThenReturn}
          snapshot={this.snapshot}
        />

        {newWebinarPreventLeave &&
          ContentPreventLeave.renderModal({
            onConfirm: this.saveThenNavigate,
            onConfirmSecondary: this.discardThenNavigate,
            onCancel: this.handleCancel
          })}
      </Fragment>
    );
  }

  renderForm = ({ fields, errors, onChange }) => {
    return (
      <ContentBasePage>
        {this.renderHeader()}

        <Grid block>
          <h2>
            <I18n label="general.content" />
          </h2>

          <Field
            className="add-webinar__field"
            label={<I18n label="general.name" />}
            name="name"
            error={errors.name && <I18n label="validations.required" />}
          >
            <Input
              type="text"
              name="name"
              placeholder={this.props.t('webinar_editor.webinar_name')}
              value={fields.name}
              onChange={onChange}
              error={!isNil(errors.name)}
            />
          </Field>

          <Field
            className="add-webinar__field"
            label={this.renderWebinarsFieldLabel()}
            name="webinars"
            error={errors.webinar && <I18n label="validations.required" />}
          >
            <Fetcher
              ref={this.fetcher}
              config={FETCHER_CONFIG}
              requester={this.fetchWebinars}
              parser={this.parseFetchWebinarsResponse}
              onSuccess={this.handleFetchWebinarsSuccess}
              onError={this.handleFetchWebinarsError}
            >
              {this.renderFetcherChildren(onChange)}
            </Fetcher>
          </Field>

          {MembershipService.isMidnight && (
            <Field className="add-webinar__field">
              <BackgroundUpload
                name="thumbnail"
                reusable
                description="webinar_editor.page_thumbnail"
                labelValue="page_content.page_thumbnail.title"
                value={fields.thumbnail}
                onCancelUpload={FileService.onCancelUpload}
                placeholderImg={PreviewPageThumbnail}
                onChange={onChange}
                entity={ENTITY_TYPE.PAGE_BACKGROUND_IMG}
                entityId={this.props.match.params.id}
                maxFileSize={MAX_PAGE_BACKGROUND_SIZE}
                setCentralError={this.onError}
              />
            </Field>
          )}
        </Grid>

        <WebinarActions
          formId="form-webinar"
          onSaveAndReturn={this.handleSaveThenReturn}
          onCancel={this.back}
        />
      </ContentBasePage>
    );
  };

  render() {
    const { loaded } = this.state;

    return (
      <ErrorBoundary>
        <MembershipConfigWatcher conditions={CONDITIONS}>
          <Redirect to="/" />
        </MembershipConfigWatcher>
        <Form
          key={this.key}
          ref={this.form}
          id="form-webinar"
          className="add-webinar"
          initial={this.snapshot}
          schema={FORM_SCHEMA}
          onSubmit={this.handleSave}
          onChange={this.handleChange}
        >
          {this.renderForm}
        </Form>
        {!loaded && <Spinner mask />}

        {this.renderPreventLeave()}
      </ErrorBoundary>
    );
  }
  // #endregion
}

AddWebinar.propTypes = AddWebinarPropTypes;

export default AddWebinar;
